/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { Effect, WorkOrderTask, WorkOrderTaskMessage } from "../../utils/Interfaces";
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import { technicianTaskIcon } from "../../components/general/IconSwitch";
import {
    AccessTime,
    AddComment,
    Cancel,
    CheckCircle,
    Close,
    DateRange,
    Delete,
    Edit,
    Person,
    QuestionAnswer,
    Send,
} from "@mui/icons-material";
import {
    useCompleteWorkOrderTask,
    useCreateWorkOrderTaskMessage,
    useDeleteWorkOrderTask,
    useEditWorkOrderTask,
} from "./workOrderHooks";
import { generateUuidNative } from "../../utils/Functions";
import WorkOrderTaskMessageEditor from "./WorkOrderTaskMessageEditor";

export interface WorkOrderTaskEditorProps {
    workOrderId: number;
    task: WorkOrderTask;
    allowTaskEdit: boolean;

    // TODO These are hacks that are used to connect React Query with the old querying implementation on the technician dashboard.
    additionalAction?: Effect;
}

const WorkOrderTaskEditor = (props: WorkOrderTaskEditorProps) => {
    const { workOrderId, task, allowTaskEdit, additionalAction } = props;

    const darkTheme: boolean = localStorage.getItem("techwiseTheme") === "dark";

    const [editTask, setEditTask] = useState<boolean>(false);
    const [label, setLabel] = useState<string>(task.label);
    const [details, setDetails] = useState<string>(task.details);

    const [showMessages, setShowMessages] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");

    const { mutate: createWorkOrderTaskMessage, status: workOrderTaskMessageCreateStatus } = useCreateWorkOrderTaskMessage(
        workOrderId,
        task.id,
        additionalAction === undefined ? () => {} : additionalAction
    );
    const { mutate: editWorkOrderTask, status: workOrderTaskEditStatus } = useEditWorkOrderTask(workOrderId, task.id);
    const { mutate: deleteWorkOrderTask, status: workOrderTaskDeletionStatus } = useDeleteWorkOrderTask(workOrderId, task.id);
    const { mutate: completeWorkOrderTask, status: workOrderTaskMarkCompletionStatus } = useCompleteWorkOrderTask(
        workOrderId,
        task.id,
        additionalAction === undefined ? () => {} : additionalAction
    );

    function handleCreateTaskMessage() {
        createWorkOrderTaskMessage({ message: message });
        setMessage("");
    }

    function handleEditTask() {
        editWorkOrderTask({ label: label, details: details });
        setEditTask(false);
    }

    return (
        <Box sx={{ background: darkTheme ? "#333333" : "#EEEEEE", borderRadius: 2, mb: 1, pl: 2, pr: 2, pt: 1, pb: 1 }}>
            <Stack direction="row" justifyContent="space-between">
                <Box sx={{ width: "100%" }}>
                    <Stack direction="row" justifyContent="flex-start" sx={{ width: "100%" }}>
                        {!editTask && (
                            <Stack direction="column" justifyContent="center">
                                <Typography>{task.label}</Typography>
                            </Stack>
                        )}
                        {editTask && (
                            <TextField
                                size="small"
                                helperText={label.length === 0 ? "Punchlist item cannot be blank" : null}
                                fullWidth
                                placeholder="Update punchlist item"
                                value={label}
                                onChange={(event) => setLabel(event.target.value)}
                                InputProps={{
                                    style: {
                                        borderRadius: 10,
                                    },
                                }}
                            />
                        )}
                        {editTask && (
                            <Stack direction="row" justifyContent="flex-start" sx={{ ml: 2 }}>
                                <Stack direction="column" justifyContent="flex-start">
                                    <Tooltip title="Discard changes">
                                        <IconButton
                                            onClick={() => {
                                                setEditTask(false);
                                                setLabel(task.label);
                                                setDetails(task.details);
                                            }}
                                        >
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Stack direction="column" justifyContent="flex-start">
                                    <Tooltip title="Save changes">
                                        <IconButton
                                            onClick={() => handleEditTask()}
                                            disabled={label.trim() === ""}
                                            color="primary"
                                        >
                                            <CheckCircle fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        )}
                        {!editTask && workOrderTaskEditStatus !== "pending" && workOrderTaskDeletionStatus !== "pending" && (
                            <Stack direction="row" justifyContent="flex-start" sx={{ ml: 2 }}>
                                <Tooltip title="Edit punchlist item">
                                    <IconButton disabled={!allowTaskEdit} onClick={() => setEditTask(true)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete punchlist item">
                                    <IconButton color="error" disabled={!allowTaskEdit} onClick={() => deleteWorkOrderTask()}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )}
                        {(workOrderTaskEditStatus === "pending" || workOrderTaskDeletionStatus === "pending") && (
                            <CircularProgress size={16} sx={{ mt: 1.25, mr: 1, ml: 3, mb: 1.25 }} />
                        )}
                    </Stack>
                    {!editTask && (
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: task.details.trim() === "" ? 0 : 2 }}>
                            {task.details.trim() === "" ? "--" : task.details}
                        </Typography>
                    )}
                    {editTask && (
                        <TextField
                            sx={{ mt: 2 }}
                            size="small"
                            multiline
                            maxRows={4}
                            fullWidth
                            placeholder="Add optional details for technicians"
                            value={details}
                            onChange={(event) => setDetails(event.target.value)}
                            InputProps={{
                                style: {
                                    borderRadius: 10,
                                },
                            }}
                        />
                    )}
                </Box>
                <Box>
                    <Stack direction="row" justifyContent="space-between">
                        <Divider orientation="vertical" sx={{ ml: 2, mr: 2, height: 40 }} />
                        {task.messages.length === 0 && (
                            <Tooltip title={showMessages ? "Close task chat" : "Open task chat"}>
                                <IconButton onClick={() => setShowMessages(!showMessages)}>
                                    {showMessages ? <Close /> : <AddComment />}
                                </IconButton>
                            </Tooltip>
                        )}
                        {task.messages.length > 0 && (
                            <Box>
                                {showMessages && (
                                    <Tooltip title="Close task chat">
                                        <IconButton onClick={() => setShowMessages(!showMessages)}>
                                            <Close />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {!showMessages && (
                                    <Tooltip title="Open task chat">
                                        <IconButton onClick={() => setShowMessages(!showMessages)}>
                                            <Badge badgeContent={task.messages.length} color="primary">
                                                <QuestionAnswer />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                        <Stack direction="column" justifyContent="center" sx={{ ml: 2 }}>
                            {technicianTaskIcon(task.completed_timestamp !== null ? 1 : 3)}
                        </Stack>
                    </Stack>
                    {task.completed_timestamp === null && (
                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                            {workOrderTaskMarkCompletionStatus !== "pending" && (
                                <Tooltip title="Complete punchlist item">
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={() => completeWorkOrderTask()}
                                    >
                                        Complete
                                    </Button>
                                </Tooltip>
                            )}
                            {workOrderTaskMarkCompletionStatus === "pending" && (
                                <CircularProgress size={16} sx={{ mr: 2, mt: 1, mb: 1 }} />
                            )}
                        </Stack>
                    )}
                    {task.completed_timestamp !== null && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="success.main" textAlign="right">
                                Completed By
                            </Typography>
                            <Stack direction="row" justifyContent="flex-end">
                                <Person fontSize="small" sx={{ transform: "scale(0.75)" }} />
                                <Stack direction="column" justifyContent="cemter">
                                    <Typography variant="caption" sx={{ mt: 0.25 }}>
                                        {task.checked_by.first_name}&nbsp;{task.checked_by.last_name}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="column" justifyContent="flex-start">
                                <Stack direction="row" justifyContent="flex-end">
                                    <DateRange fontSize="small" sx={{ transform: "scale(0.75)" }} />
                                    <Stack direction="column" justifyContent="cemter">
                                        <Typography variant="caption" sx={{ mt: 0.25 }}>
                                            &nbsp;{new Date(task.completed_timestamp).toLocaleDateString()}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack direction="row" justifyContent="flex-end">
                                    <AccessTime fontSize="small" sx={{ transform: "scale(0.75)" }} />
                                    <Stack direction="column" justifyContent="cemter">
                                        <Typography variant="caption" sx={{ mt: 0.25 }}>
                                            &nbsp;
                                            {new Date(task.completed_timestamp).toLocaleTimeString([], {
                                                hour: "numeric",
                                                minute: "2-digit",
                                            })}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Stack>
            {showMessages && (
                <Box sx={{ mt: 2 }}>
                    {task.messages.map((taskMessage: WorkOrderTaskMessage) => {
                        return (
                            <WorkOrderTaskMessageEditor
                                workOrderId={workOrderId}
                                taskMessage={taskMessage}
                                key={generateUuidNative()}
                                additionalAction={additionalAction}
                            />
                        );
                    })}
                    <TextField
                        sx={{
                            mt: 3,
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: darkTheme ? "#404040" : "#FFFFFF",
                                "&:hover": {
                                    backgroundColor: darkTheme ? "#404040" : "#FFFFFF",
                                },
                                "&.Mui-focused": {
                                    backgroundColor: darkTheme ? "#404040" : "#FFFFFF",
                                },
                            },
                        }}
                        placeholder="Comment on punchlist item"
                        size="small"
                        multiline={true}
                        maxRows={4}
                        fullWidth
                        value={message}
                        onChange={(event) => setMessage(event.target.value)}
                        InputProps={{
                            style: {
                                borderRadius: 10,
                            },
                            endAdornment: (
                                <InputAdornment position="end" sx={{ mr: -1 }}>
                                    {!(workOrderTaskMessageCreateStatus === "pending") && (
                                        <IconButton
                                            onClick={() => handleCreateTaskMessage()}
                                            color="primary"
                                            disabled={message.trim() === ""}
                                        >
                                            <Send />
                                        </IconButton>
                                    )}
                                    {workOrderTaskMessageCreateStatus === "pending" && (
                                        <CircularProgress size={18} sx={{ mr: 2 }} />
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default WorkOrderTaskEditor;
